import {HTMLAttributes, useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import styled from 'styled-components';
import {getGqlErrorDetailData, getGqlErrorMessage} from '@/api/request';
import {useRequestPwdResetCode, useRequestSmsCode} from '@/api/session';
import toast from '@/components/HotToast';
import {CHANNEL_TALK} from '@/router';
import {FlexFullGrow, LinkText, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {maskingEmailAddress} from '@/utils';
import {Button} from '../Button';
import {Modal} from '../Modal';
import {TextField} from '../TextField';
import {Typography} from '../Typography';
import {PhoneVerifyCode} from './PhoneVerifyCode';
import {InlineFieldWrapper, InputContainer, TextFieldLabel} from './StyledComponents';
import VerifiedIcon from './verified-icon.svg';

interface Inputs {
  cellPhone: string;
}

interface Values {
  cellPhone?: string;
  smsVerificationId?: string;
}

interface IProps extends HTMLAttributes<HTMLDivElement> {
  cellPhone?: string;
  email?: string;
  type?: 'JOIN' | 'PWD_CHANGE';
  onChanged(data: Values): void;
  onVerified(data: {smsVerificationId: string}): void;
  onComplete(): void;
  onClear?(): void;
}

export const PhoneVerifyStep = ({
  email,
  cellPhone,
  type,
  onChanged,
  onVerified,
  onComplete,
  onClear,
  ...props
}: IProps) => {
  const {
    control,
    watch,
    setFocus,
    handleSubmit,
    setError,
    formState: {isValid, errors},
  } = useForm<Inputs>({mode: 'all', defaultValues: {cellPhone}});
  const [verified, setVerified] = useState<boolean>(false);

  const [values, setValues] = useState<Values>({cellPhone});
  const {
    mutate: requestJoinCode,
    data: joinCodeData,
    error: joinCodeError,
    isPending: isJoinCodePending,
  } = useRequestSmsCode();
  const {
    mutate: requestPwdCode,
    data: pwdCodeData,
    error: pwdCodeError,
    isPending: isPwdCodePending,
  } = useRequestPwdResetCode();

  const [existAccount, setExistAccount] = useState<string>();

  const codeData = (type === 'PWD_CHANGE' ? pwdCodeData : joinCodeData)?.result;
  const smsVerificationId = codeData?.smsVerificationId;

  const isQuotaError = (serverError: Error | null) => {
    return (
      serverError &&
      getGqlErrorMessage(serverError) === '인증 번호 전송 횟수를 초과했습니다.'
    );
  };

  const requestCodeError = type === 'PWD_CHANGE' ? pwdCodeError : joinCodeError;

  const quotaError = isQuotaError(requestCodeError);

  const setCellPhoneError = (serverError: Error) => {
    if (!isQuotaError(serverError)) {
      const errorMessage = getGqlErrorMessage(serverError);

      if (errorMessage) {
        setError('cellPhone', {
          type: 'server-error',
          message: errorMessage,
        });
      }
    }
  };

  const onSubmit = () => {
    if (!isValid || !values?.cellPhone) {
      return;
    }

    onChanged(values);

    if (type === 'PWD_CHANGE') {
      if (email) {
        requestPwdCode(
          {
            email,
            smsVerificationId,
            cellPhone: values.cellPhone,
          },
          {
            onError: (serverError) => {
              if (serverError) {
                setCellPhoneError(serverError);
              }
            },
          }
        );
      }
    } else {
      requestJoinCode(
        {
          smsVerificationId,
          cellPhone: values.cellPhone,
        },
        {
          onError: (serverError) => {
            if (serverError) {
              const errorDetail = getGqlErrorDetailData(serverError);

              if (errorDetail?.extra?.email) {
                setExistAccount(maskingEmailAddress(errorDetail.extra.email));
              } else {
                setCellPhoneError(serverError);
              }
            }
          },
        }
      );
    }
  };

  const handleVerified = () => {
    if (smsVerificationId) {
      onVerified({
        smsVerificationId,
      });
      setVerified(true);
      toast.success('인증되었습니다.');
    }
  };

  useEffect(() => {
    setFocus('cellPhone');
  }, [setFocus]);

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value);
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  const hasCodeData = !!smsVerificationId;
  const error = errors['cellPhone']?.message;

  return (
    <Root {...props}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <TextFieldLabel error={!!error?.length}>휴대전화 번호</TextFieldLabel>
        <div>
          <InlineFieldWrapper>
            <Controller
              control={control}
              name="cellPhone"
              rules={{
                required: '휴대전화 번호를 입력해 주세요.',
                pattern: {
                  value: /^010[-]?[0-9]{3,4}[-]?[0-9]{4}$/,
                  message: '올바른 휴대전화 번호를 입력해 주세요.',
                },
                maxLength: 11,
                minLength: {
                  value: 11,
                  message: '올바른 휴대전화 번호를 입력해 주세요.',
                },
              }}
              render={({field}) => (
                <FieldWrapper>
                  <TextField
                    ref={field.ref}
                    maxLength={11}
                    fullWidth
                    variant="xlarge"
                    placeholder="휴대전화번호 11자리"
                    value={field.value}
                    error={!!error?.length}
                    disabled={!!quotaError}
                    pattern="[0-9]*"
                    onChange={(event) => {
                      field.onChange(event.target.value.replace(/\D/g, ''));
                    }}
                  />
                  {verified && (
                    <VerifiedMark src={VerifiedIcon} alt="인증 완료" />
                  )}
                  {!!quotaError && (
                    <PaddingBox top={8}>
                      <Typography
                        variant="medium12"
                        lineHeight="20px"
                        color={palette.red400}
                        fontWeight="400"
                        as="div"
                      >
                        인증번호 발송 전송 횟수를 초과했습니다.
                        <br />
                        잠시 후에 다시 시도해 주세요.
                      </Typography>
                    </PaddingBox>
                  )}
                </FieldWrapper>
              )}
            />
            <Button
              disabled={
                !isValid ||
                verified ||
                isJoinCodePending ||
                isPwdCodePending ||
                quotaError
              }
              variant="fill"
              color="secondary"
              size="mediumLarge"
              fullWidth
            >
              {isJoinCodePending || isPwdCodePending
                ? '요청 중...'
                : !!existAccount || hasCodeData
                ? '재전송'
                : '인증 번호 전송'}
            </Button>
          </InlineFieldWrapper>
          {!!error?.length && <ErrorText>{error}</ErrorText>}
        </div>
      </form>
      {!!existAccount && (
        <Modal
          title="가입 내역 확인"
          width={328}
          onClose={() => setExistAccount(undefined)}
        >
          <PaddingBox spacing={24}>
            <Typography
              variant="medium14"
              color={palette.gray600}
              as="div"
              align="center"
            >
              이미 가입된 메일 주소가 있습니다.
            </Typography>
            <PaddingBox top={16} bottom={16}>
              <ExistEmailAddress>{existAccount}</ExistEmailAddress>
            </PaddingBox>
            <Typography variant="medium14" color={palette.gray600} as="div">
              마이프차 지도 서비스는 휴대전화 번호 하나에 한 계정만 가입이
              가능해요.
            </Typography>
            <PaddingBox top={24}>
              <Button
                variant="fill"
                color="secondary"
                size="mediumLarge"
                fullWidth
                onClick={onClear}
              >
                로그인하러 가기
              </Button>
            </PaddingBox>
          </PaddingBox>
        </Modal>
      )}
      {!verified && hasCodeData && (
        <PhoneVerifyCode
          cellPhone={values.cellPhone}
          smsData={codeData}
          onComplete={handleVerified}
        />
      )}
      <FlexFullGrow />
      {type === 'PWD_CHANGE' && (
        <PaddingBox bottom={32}>
          <Typography
            variant="medium14"
            lineHeight="20px"
            align="center"
            as="div"
            color={palette.gray600}
          >
            가입 이후 휴대전화 번호가 바뀌어 재설정이 안된다면
            <br />
            <LinkText href={CHANNEL_TALK} target="_blank" rel="noreferrer">
              고객센터
            </LinkText>
            에 문의해 주세요.
          </Typography>
        </PaddingBox>
      )}
      <Button
        type="button"
        disabled={!verified}
        variant="fill"
        color="primary"
        size="mediumLarge"
        fullWidth
        onClick={onComplete}
      >
        다음
      </Button>
    </Root>
  );
};

const Root = styled(InputContainer)`
  gap: 8px;
`;

const FieldWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const VerifiedMark = styled.img`
  position: absolute;
  right: 16px;
  top: 16px;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const ExistEmailAddress = styled.div`
  text-align: center;
  color: ${palette.primary400};
  font-weight: 600;
  font-size: 18px;
  line-height: 1;
`;

const ErrorText = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  margin-top: 8px;
  color: ${palette.red400};
`;
